import { useEffect, useRef, useState } from 'react'
import { getEchoClient, useEchoClient } from './echoClient'

export type TestPresenceUser = { id: number; name: string }

const ECHO_DEBUG =
  import.meta.env.DEV || String(import.meta.env.VITE_ECHO_DEBUG || '') === 'true'

/** Интервал опроса GET /tests/{id}, когда WebSocket недоступен (страховка для ученика). */
const DISCONNECTED_POLL_MS = 4000
/** Не опрашивать API, пока Echo может успеть подключиться после mount. */
const WS_CONNECT_GRACE_MS = 2500

function echoDevLog(...args: unknown[]) {
  if (ECHO_DEBUG) {
    console.info('[Echo]', ...args)
  }
}

/** Короткий сигнал при входе другого пользователя в presence-канал теста (Web Audio API). */
function playJoinSound() {
  const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AC) return
  const ctx = new AC()
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.connect(gain)
  gain.connect(ctx.destination)
  osc.type = 'sine'
  osc.frequency.setValueAtTime(880, ctx.currentTime)
  gain.gain.setValueAtTime(0.1, ctx.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4)
  osc.start(ctx.currentTime)
  osc.stop(ctx.currentTime + 0.4)
}

function normalizePresenceUsers(raw: unknown[]): TestPresenceUser[] {
  return raw
    .map((u) => {
      const o = u as { id?: unknown; name?: unknown }
      return {
        id: Number(o.id),
        name: String(o.name ?? ''),
      }
    })
    .filter((u) => Number.isFinite(u.id))
}

function leaveTestPresenceChannel(echo: ReturnType<typeof getEchoClient>, channelName: string) {
  try {
    echo.leave(channelName)
  } catch {
    // ignore
  }
}

export function useTestRealtime(
  testId: number | null,
  onUpdate: (type: 'started' | 'answer' | 'item' | 'completed' | 'cancelled') => void,
  options?: {
    authUserId?: number | null
    onAnswerDraft?: (payload: { item_id: number; user_id: number; text_preview: string }) => void
    onTeacherItemChanged?: (payload: { participant_id: number | null; item_id: number }) => void
    onTeacherEvaluationChanged?: (payload: { item_id: number; answer_id: number; is_correct: boolean }) => void
    /**
     * Подсказки карточки прогона добавили или удалили (`test.card.clues.changed`).
     * Текста в нагрузке нет — подписчик сам перечитывает прогон. Без обработчика
     * событие перечитывает прогон как обычное обновление позиции.
     */
    onCardCluesChanged?: (payload: { card_id: number }) => void
  },
) {
  const [isConnected, setIsConnected] = useState(false)
  const [presentUsers, setPresentUsers] = useState<TestPresenceUser[]>([])
  const onUpdateRef = useRef(onUpdate)
  const authUserIdRef = useRef(options?.authUserId)
  const onAnswerDraftRef = useRef(options?.onAnswerDraft)
  const onTeacherItemChangedRef = useRef(options?.onTeacherItemChanged)
  const onTeacherEvaluationChangedRef = useRef(options?.onTeacherEvaluationChanged)
  const onCardCluesChangedRef = useRef(options?.onCardCluesChanged)

  onUpdateRef.current = onUpdate
  authUserIdRef.current = options?.authUserId
  onAnswerDraftRef.current = options?.onAnswerDraft
  onTeacherItemChangedRef.current = options?.onTeacherItemChanged
  onTeacherEvaluationChangedRef.current = options?.onTeacherEvaluationChanged
  onCardCluesChangedRef.current = options?.onCardCluesChanged

  // Клиент — зависимость эффекта: после пересоздания Echo подписка иначе
  // осталась бы на отключённом сокете.
  const echo = useEchoClient()

  useEffect(() => {
    if (!Number.isFinite(testId)) {
      setIsConnected(false)
      setPresentUsers([])
      return
    }

    let didJoin = false
    const channelName = `tests.${testId}`
    const presenceKey = `presence-${channelName}`

    setIsConnected(false)
    setPresentUsers([])

    const connector = echo.connector as { channels?: Record<string, unknown> }
    if (connector.channels?.[presenceKey]) {
      leaveTestPresenceChannel(echo, channelName)
    }

    const channel = echo.join(channelName)

    type ChannelWithLifecycle = typeof channel & {
      subscribed?: (cb: () => void) => typeof channel
      error?: (cb: (err: unknown) => void) => typeof channel
    }
    const ch = channel as ChannelWithLifecycle

    if (typeof ch.subscribed === 'function') {
      ch.subscribed(() => {
        setIsConnected(true)
        echoDevLog(
          `Подписка на presence-${channelName} подтверждена (см. также POST /api/broadcasting/auth в Network)`,
        )
      })
    } else {
      setIsConnected(true)
      echoDevLog(`presence-${channelName}: колбэка subscribed() нет — подписка считается активной`)
    }

    if (typeof ch.error === 'function') {
      ch.error((err) => {
        setIsConnected(false)
        console.error('[Echo] Ошибка подписки на presence-канал', channelName, err)
      })
    }

    channel
      .here((users: unknown[]) => {
        setPresentUsers(normalizePresenceUsers(users))
      })
      .joining((user: unknown) => {
        const u = normalizePresenceUsers([user])[0]
        if (!u) return
        const selfId = authUserIdRef.current
        if (selfId != null && u.id === selfId) return
        playJoinSound()
        setPresentUsers((prev) => (prev.some((x) => x.id === u.id) ? prev : [...prev, u]))
      })
      .leaving((user: unknown) => {
        const u = normalizePresenceUsers([user])[0]
        if (!u) return
        setPresentUsers((prev) => prev.filter((x) => x.id !== u.id))
      })

    // Имена — ровно те, что возвращает broadcastAs() серверных событий
    // (TestStarted -> test.started, TestCompleted -> test.completed).
    // Прежние '.test.run.started'/'.test.run.completed' сервер не шлёт никогда,
    // и ни линтер, ни type-check такую подписку не ловят: она просто молчит
    channel.listen('.test.started', () => onUpdateRef.current('started'))
    channel.listen('.test.answer.submitted', () => onUpdateRef.current('answer'))
    channel.listen('.test.item.completed', () => onUpdateRef.current('item'))
    channel.listen('.test.completed', () => onUpdateRef.current('completed'))
    // Урок отменён — прогон закрыт без результата (см. LessonTestsFinalizeService).
    channel.listen('.test.cancelled', () => onUpdateRef.current('cancelled'))
    channel.listen('.test.item.usage.changed', () => onUpdateRef.current('item'))
    channel.listen('.test.hint.created', () => onUpdateRef.current('item'))
    channel.listen('.test.teacher.evaluation.changed', (raw: unknown) => {
      const p = raw as { item_id?: unknown; answer_id?: unknown; is_correct?: unknown }
      const item_id = Number(p.item_id)
      const answer_id = Number(p.answer_id)
      const is_correct = Boolean(p.is_correct)
      if (Number.isFinite(item_id) && Number.isFinite(answer_id)) {
        onTeacherEvaluationChangedRef.current?.({ item_id, answer_id, is_correct })
      }
      onUpdateRef.current('item')
    })
    channel.listen('.test.teacher.item.changed', (raw: unknown) => {
      const p = raw as { participant_id?: unknown; item_id?: unknown }
      const participant_id =
        p.participant_id === null || p.participant_id === undefined ? null : Number(p.participant_id)
      const item_id = Number(p.item_id)
      if (Number.isFinite(item_id)) {
        onTeacherItemChangedRef.current?.({
          participant_id: Number.isFinite(participant_id as number) ? (participant_id as number) : null,
          item_id,
        })
      }
      onUpdateRef.current('item')
    })
    // Подсказку карточки добавил или удалил учитель либо ученик (CardClueService).
    // Без этой подписки вторая сторона видела подсказку только после перезагрузки.
    channel.listen('.test.card.clues.changed', (raw: unknown) => {
      const p = raw as { card_id?: unknown }
      const card_id = Number(p.card_id)
      const handler = onCardCluesChangedRef.current
      if (handler && Number.isInteger(card_id) && card_id > 0) {
        handler({ card_id })
        return
      }
      onUpdateRef.current('item')
    })
    channel.listen('.test.answer.draft.changed', (raw: unknown) => {
      const p = raw as { item_id?: unknown; user_id?: unknown; text_preview?: unknown }
      const item_id = Number(p.item_id)
      const user_id = Number(p.user_id)
      const text_preview = String(p.text_preview ?? '')
      if (Number.isFinite(item_id) && Number.isFinite(user_id)) {
        onAnswerDraftRef.current?.({ item_id, user_id, text_preview })
      }
    })

    didJoin = true

    return () => {
      if (didJoin) {
        leaveTestPresenceChannel(echo, channelName)
        didJoin = false
      }
      setIsConnected(false)
      setPresentUsers([])
    }
  }, [echo, testId])

  useEffect(() => {
    if (!Number.isFinite(testId) || isConnected) return

    let intervalId: ReturnType<typeof window.setInterval> | undefined
    const graceTimer = window.setTimeout(() => {
      onUpdateRef.current('item')
      intervalId = window.setInterval(() => {
        onUpdateRef.current('item')
      }, DISCONNECTED_POLL_MS)
    }, WS_CONNECT_GRACE_MS)

    return () => {
      window.clearTimeout(graceTimer)
      if (intervalId !== undefined) {
        window.clearInterval(intervalId)
      }
    }
  }, [testId, isConnected])

  return { isConnected, presentUsers }
}

